// hooks/useLastViewedProducts.js
// Hook to fetch user's last viewed products
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useAuthStore } from "../stores/authStore";

/**
 * Hook to get user's last viewed products
 * @param {number} limit - Maximum number of products to return (default: 20)
 * @returns {Object} Query result with products data
 */
export const useLastViewedProducts = (limit = 20) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ["lastViewedProducts", limit],
    queryFn: async () => {
      try {
        const response = await apiService.getLastViewedProducts(limit);
        // Handle different response formats
        if (response.status === "success" && response.data) {
          // Extract products from UserView objects
          const views = response.data;
          // If views contain product objects, extract them
          const products = views.map((view) => {
            if (view.product) {
              return {
                ...view.product,
                viewedAt: view.viewedAt,
                viewCount: view.viewCount,
              };
            }
            return view;
          });
          return products;
        }
        // If response is already an array of products
        if (Array.isArray(response)) {
          return response;
        }
        if (Array.isArray(response?.data)) {
          return response.data;
        }
        return [];
      } catch (error) {
        // Don't log errors if user is not authenticated - this is expected
        if (isAuthenticated) {
          console.error("Error fetching last viewed products:", error);
        }
        return [];
      }
    },
    enabled: !!isAuthenticated, // Only fetch if user is authenticated
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

export default useLastViewedProducts;

